import React, { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TextUsWidget() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({ phone: "", enabled: false });
  const [form, setForm] = useState({ name: "", phone: "", message: "", interests: [], company_website: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  const INTEREST_OPTIONS = [
    "Regular Cleaning", "Deep Cleaning", "Move In/Out",
    "Office Cleaning", "One-Time Cleaning", "Other"
  ];

  useEffect(() => {
    axios.get(`${API}/sms/text-us-config`).then(r => setConfig(r.data)).catch(() => {}).finally(() => setConfigLoaded(true));
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  const toggleInterest = (interest) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone) return;

    // On mobile, open native SMS
    if (isMobile && config.phone) {
      const body = `Hi, I'm ${form.name || "interested"}. ${form.interests.length ? "Interested in: " + form.interests.join(", ") + ". " : ""}${form.message}`;
      window.location.href = `sms:${config.phone}?body=${encodeURIComponent(body)}`;
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/sms/text-us`, {
        visitor_phone: form.phone,
        name: form.name,
        message: form.message,
        interests: form.interests,
        company_website: form.company_website
      });
      setSubmitted(true);
    } catch (err) {
      if (err?.response?.status === 429) {
        alert("Too many messages sent. Please wait a few minutes and try again.");
      } else {
        alert("Failed to send. Please try again or call (817) 277-7387.");
      }
    }
    setSending(false);
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", message: "", interests: [], company_website: "" });
    setSubmitted(false);
    setOpen(false);
  };

  if (config.enabled === false && !config.phone) {
    // Widget always shows - form submissions save leads even without SMS configured
  }

  // Hide the widget entirely when SMS credit is depleted ($0 balance) — the
  // backend returns enabled:false. Wait for config to load to avoid a flash.
  if (configLoaded && config.enabled === false) {
    return null;
  }

  return (
    <>
      {/* Floating Tab Button */}
      {!open && (
        <button
          className="textus-tab"
          onClick={() => setOpen(true)}
          data-testid="textus-tab"
          aria-label="Text Us"
        >
          <i className="fa fa-commenting"></i>
          <span>Text Us</span>
        </button>
      )}

      {/* Widget Panel */}
      {open && (
        <div className="textus-panel" data-testid="textus-panel">
          <div className="textus-header">
            <div className="textus-header-title">
              <i className="fa fa-commenting"></i>
              <span>Text Us</span>
            </div>
            <button className="textus-close" onClick={resetForm} data-testid="textus-close">
              <i className="fa fa-times"></i>
            </button>
          </div>

          {submitted ? (
            <div className="textus-body textus-success" data-testid="textus-success">
              <div className="textus-success-icon"><i className="fa fa-check-circle"></i></div>
              <h3>Message Sent!</h3>
              <p>We&apos;ll text you back shortly at the number you provided.</p>
              <button className="textus-submit-btn" onClick={resetForm}>Close</button>
            </div>
          ) : (
            <form className="textus-body" onSubmit={handleSubmit}>
              {/* Honeypot: hidden from humans; bots auto-fill it and get rejected */}
              <input
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.company_website}
                onChange={e => setForm({ ...form, company_website: e.target.value })}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                data-testid="textus-hp"
              />
              <div className="textus-field">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  data-testid="textus-name"
                />
              </div>
              <div className="textus-field">
                <input
                  type="tel"
                  placeholder="Your Phone Number *"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  required
                  data-testid="textus-phone"
                />
              </div>

              <div className="textus-interests" data-testid="textus-interests">
                <label className="textus-interests-label">I&apos;m interested in:</label>
                <div className="textus-pills">
                  {INTEREST_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      className={`textus-pill ${form.interests.includes(opt) ? "active" : ""}`}
                      onClick={() => toggleInterest(opt)}
                      data-testid={`textus-pill-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="textus-field">
                <textarea
                  placeholder="Message (optional)"
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value.slice(0, 160) })}
                  rows="3"
                  data-testid="textus-message"
                />
                <div className="textus-char-count">{form.message.length}/160 chars</div>
              </div>

              <button
                type="submit"
                className="textus-submit-btn"
                disabled={sending || !form.phone}
                data-testid="textus-submit"
              >
                {sending ? (
                  <><i className="fa fa-spinner fa-spin"></i> Sending...</>
                ) : (
                  <><i className="fa fa-paper-plane"></i> Send</>
                )}
              </button>

              <div className="textus-disclaimer">
                By texting us, you agree to our <a href="/sms-policy" target="_blank" rel="noopener noreferrer">SMS Terms</a>. Msg & data rates may apply. Reply STOP to opt out.
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
